import type { ConfidenceReport } from '@/lib/confidence';
import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface CanonicalPersonality {
  user_id: string;
  mbti: string | null;
  enneagram: string | null;
  big_five: Record<string, number> | null;
  socionics: string | null;
  holland: string | null;
  alignment: string | null;
  attachment: string | null;
  disc: string | null;
  sloan: string | null;
  integral_level: number | null;
  confidence_score: number;
  confidence_report: ConfidenceReport | null;
  sources_used: string[];
  /** Assessment this resolution was derived from (provenance) */
  source_assessment_id: string | null;
  /** `created_at` of the assessment above — used to detect stale canonical rows */
  derived_from_at: string | null;
  last_updated_at: string;
  version: number;
}

/**
 * Hook to read/write the server-side canonical personality record.
 * This is the single source of truth for a user's resolved personality type.
 */
export function useCanonicalPersonality(targetUserId?: string) {
  const { user } = useAuth();
  const userId = targetUserId || user?.id;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['canonical-personality', userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from('user_canonical_personality')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown) as CanonicalPersonality | null;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 min
  });

  const upsertMutation = useMutation({
    mutationFn: async (updates: Partial<Omit<CanonicalPersonality, 'user_id' | 'version'>>) => {
      if (!userId) throw new Error('No user');
      const payload: Record<string, unknown> = {
        user_id: userId,
        ...updates,
        last_updated_at: new Date().toISOString(),
      };
      // ConfidenceReport is a strict type; the generated Json type wants a
      // plain record. Widen at the DB boundary only.
      if ('confidence_report' in payload && payload.confidence_report) {
        payload.confidence_report = JSON.parse(
          JSON.stringify(payload.confidence_report),
        );
      }
      const { data, error } = await supabase
        .from('user_canonical_personality')
        .upsert(payload as never, { onConflict: 'user_id' })
        .select()
        .single();
      if (error) throw error;
      return { data, updates };
    },
    onSuccess: ({ data, updates }) => {
      const previous = query.data;
      queryClient.invalidateQueries({ queryKey: ['canonical-personality', userId] });

      // Dispatch rich personality-data-changed event with change details
      const changedFields: string[] = [];
      const newValues: Record<string, unknown> = {};
      const previousValues: Record<string, unknown> = {};

      for (const key of Object.keys(updates) as (keyof typeof updates)[]) {
        if (key === 'last_updated_at' || key === 'sources_used') continue;
        const prev = previous?.[key as keyof CanonicalPersonality];
        const next = updates[key];
        if (prev !== next && next !== undefined) {
          changedFields.push(key as string);
          newValues[key as string] = next;
          previousValues[key as string] = prev;
        }
      }

      if (changedFields.length > 0) {
        window.dispatchEvent(new CustomEvent('personality-data-changed', {
          detail: { changedFields, newValues, previousValues, userId },
        }));
      }
    },
  });

  // Listen for personality-data-changed to also invalidate liker inference
  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: ['liker-personality-inference'] });
    };
    window.addEventListener('personality-data-changed', handler);
    return () => window.removeEventListener('personality-data-changed', handler);
  }, [queryClient]);

  return {
    canonical: query.data,
    loading: query.isLoading,
    upsertCanonical: upsertMutation.mutateAsync,
    isUpserting: upsertMutation.isPending,
  };
}
